import { DateTime } from 'luxon';
import { Calendar } from '../../ui/calendar/calendar';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Select from '../../ui/select';
import { cn } from '@/common-utils/libraries/tailwind/utils';
import DatePickedOrInfinityRange from './components/DatePickedOrInfinityRange';
import RangePicked from './components/RangePicked';

/**
 * @param root0
 * @param root0.setDateTimeValues
 * @param root0.isLocalDateAndTime
 * @param root0.isRestricted
 * @param root0.resetSelection
 * @param root0.allowRangeSelection
 * @param root0.rangeDisabled
 * @param root0.presetTime
 * @returns {JSX} DateTimePicker
 */
function DateTimePicker({
    setDateTimeValues,
    isLocalDateAndTime = undefined,
    isRestricted = undefined,
    resetSelection = undefined,
    allowRangeSelection = false,
    rangeDisabled = null,
    presetTime = null
}) {

    // State
    const [selected, setSelected] = useState({ from: DateTime.now().toJSDate(), to: DateTime.now().toJSDate() });
    const [selectedOption, setSelectedOption] = useState('select-period');
    const [from, setFrom] = useState(DateTime.now());
    const [to, setTo] = useState(DateTime.now());
    const [before, setBefore] = useState(false);
    const [after, setAfter] = useState(false);
    const [fromHour, setFromHour] = useState('00:00');
    const [toHour, setToHour] = useState('23:59');

    // Constants
    const DATE_FUTURE = DateTime.fromObject({ year: 3000, month: 1, day: 1 });
    const DATE_PAST = DateTime.fromObject({ year: 1970, month: 1, day: 1 });
    const OPTIONS = [
        { value: 'select-before-dates', name: 'date_picker.options.before' },
        { value: 'select-period', name: 'date_picker.options.select' },
        { value: 'select-after-dates', name: 'date_picker.options.after' }
    ];
    // const [limitInDays, setLimitInDays] = useState(limitInDaysProp ?? null);
    // const isOutOfRange = isRestricted && moment(to, 'DD/MM/YYYY').diff(moment(from, 'DD/MM/YYYY'), 'days') > limitInDays;
    // const fromLimit = this.removeDays(to, limitInDays);
    // const toLimit = this.addDays(from, limitInDays);
    // const isToInFuture = moment(toLimit, 'DD/MM/YYYY').diff(moment(today, 'DD/MM/YYYY')) >= 0;
    // const daysInPastLimit = this.removeDays(today, enabledDaysInPast);

    // Functions
    const resetToInitialValues = () => {
        setAfter(false);
        setBefore(false);
        setFrom(DateTime.now());
        setTo(DateTime.now());
        setFromHour('00:00');
        setToHour('23:59');
        setSelected({ from: DateTime.now().toJSDate(), to: DateTime.now().toJSDate() });
    };

    const handleDayClick = (selection) => {
        setSelected(selection);
        if (allowRangeSelection) {
            if (!before && !after) {
                const selectedFromDate = selection?.from?.toISOString();
                const luxonSelectedFromDate = selectedFromDate ? DateTime.fromISO(selectedFromDate) : null;
                setFrom(luxonSelectedFromDate);
                const selectedToDate = selection?.to?.toISOString();
                const luxonSelectedToDate = selectedToDate ? DateTime.fromISO(selectedToDate) : null;
                const diff = luxonSelectedToDate.diff(luxonSelectedFromDate, ['days']);
                if (diff.toObject().days > 30) {
                    const maxToDate = luxonSelectedFromDate.plus({ days: 30 });
                    setTo(maxToDate);
                    setSelected({ from: luxonSelectedFromDate.toJSDate(), to: maxToDate.toJSDate() });
                } else {
                    setTo(luxonSelectedToDate);
                }
            }
            else if (before) {
                setFrom(DATE_PAST);
                const selectedToDate = selection?.to?.toISOString();
                const luxonSelectedToDate = selectedToDate ? DateTime.fromISO(selectedToDate) : null;
                setTo(luxonSelectedToDate);
            }
            else if (after) {
                const selectedFromDate = selection?.from?.toISOString();
                const luxonSelectedFromDate = selectedFromDate ? DateTime.fromISO(selectedFromDate) : null;
                setFrom(luxonSelectedFromDate);
                setTo(DATE_FUTURE);
            }
        } else {
            setFrom(selection ? DateTime.fromISO(selection.toISOString()) : null);
        }
    };

    const onChangeTime = (event) => {
        const inputName = event.target.name;
        if (inputName === 'fromHour') {
            setFromHour(event.target.value);
        }
        if (inputName === 'toHour') {
            setToHour(event.target.value);
        }
    };

    const onChangeOptions = (option) => {
        setSelectedOption(option);
        if (option === 'select-before-dates') {
            setAfter(false);
            setBefore(true);
            setFrom(DATE_PAST);
            setTo(after ? from : to);
            setFromHour('00:00');
            setToHour('23:59');
            setSelected({ from: DATE_PAST, to: after ? from : to });
        }
        else if (option === 'select-after-dates') {
            setAfter(true);
            setBefore(false);
            setFrom(before ? to : from);
            setTo(DATE_FUTURE);
            setFromHour('00:00');
            setToHour('23:59');
            setSelected({ from: before ? to : from, to: DATE_FUTURE });
        }
        else {
            resetToInitialValues();
        }
    };


    const updateDateTime = (dateToUpdate, time, isEndDate = false) => {
        if (!dateToUpdate) {
            return null;
        }

        const [hour, minute] = presetTime ? presetTime.split(':') : time.split(':');
        dateToUpdate = dateToUpdate.set({
            hour,
            minute,
            second: isEndDate ? 59 : 0,
            millisecond: isEndDate ? 999 : 0
        });

        return isLocalDateAndTime ? dateToUpdate.toFormat('yyyy-MM-dd\'T\'HH:mm:ss') : dateToUpdate.toUTC().toISO();
    };

    // Hooks
    useEffect(() => {
        setDateTimeValues({
            startDateSelected: updateDateTime(from, fromHour, false),
            endDateSelected: updateDateTime(to, toHour, true),
            beforeSelected: before,
            afterSelected: after,
            startTimeSelected: fromHour,
            endTimeSelected: toHour
        });
    }, [from, to, fromHour, toHour, before, after]);

    useEffect(() => {
        if (resetSelection) {
            resetToInitialValues();
            setSelectedOption('select-period');
        }
    }, [resetSelection]);


    return (
        <div className='flex flex-col gap-2 items-center'>
            <Calendar
                mode={allowRangeSelection ? 'range' : 'single'}
                selected={selected}
                onSelect={handleDayClick}
                disabled={rangeDisabled}
            />
            <div className={cn('time-picker flex flex-col gap-2 items-center', !isLocalDateAndTime && 'no-local-info')}>
                {
                    allowRangeSelection &&
                    <div className='flex'>
                        <Select
                            options={OPTIONS}
                            onChange={onChangeOptions}
                            value={selectedOption}
                            className='items-center w-40'
                            noValidateIcon={true}
                        />
                    </div>
                }
                {
                    (before || after || !allowRangeSelection) && !presetTime &&
                    <DatePickedOrInfinityRange
                        before={before}
                        after={after}
                        from={from}
                        to={to}
                        onChangeTime={onChangeTime}
                        fromHour={fromHour}
                        toHour={toHour}
                        allowRangeSelection={allowRangeSelection}
                    />
                }
                {
                    allowRangeSelection &&
                    <RangePicked
                        before={before}
                        after={after}
                        from={from}
                        to={to}
                        onChangeTime={onChangeTime}
                        fromHour={fromHour}
                        toHour={toHour}
                        isLocalDateAndTime={isLocalDateAndTime}
                        isRestricted={isRestricted}
                        isOutOfRange={null}
                        toLimit={null}
                    />
                }
            </div >
        </div>
    );
}

export default DateTimePicker;

DateTimePicker.propTypes = {
    setDateTimeValues: PropTypes.func.isRequired,
    isLocalDateAndTime: PropTypes.bool,
    isRestricted: PropTypes.bool,
    resetSelection: PropTypes.bool,
    allowRangeSelection: PropTypes.bool,
    rangeDisabled: PropTypes.func,
    presetTime: PropTypes.string
};